import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, Phone, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";

export default function Contact({ content }) {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim() || !form.message.trim()) {
      toast.error("Please fill name, mobile and message");
      return;
    }
    if (!/^[0-9+\-\s]{8,15}$/.test(form.mobile.trim())) {
      toast.error("Please enter a valid mobile number");
      return;
    }
    try {
      setLoading(true);
      await api.post("/enquiries", form);
      toast.success("Thanks! We'll reach out within a day.");
      setForm({ name: "", mobile: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="section">
      <div className="container-z grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="label-mono mb-3">Talk to us</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A231E]">
            Got a question?<br />
            We're <span className="text-[#C86646]">listening</span>.
          </h2>
          <p className="mt-4 text-base text-[#5C6B62] max-w-md">
            Whether you're a broker, owner or city partner — drop us a message and our team will
            get back within 24 hours.
          </p>

          <div className="mt-7 space-y-4">
            <div className="flex items-center gap-3 text-[#1A231E]">
              <div className="w-10 h-10 rounded-xl bg-[#EAF1ED] border border-[#C8DDD2] flex items-center justify-center">
                <Phone className="w-4 h-4 text-[#1E3F33]" />
              </div>
              <span className="font-medium">{content.contact_phone}</span>
            </div>
            <div className="flex items-center gap-3 text-[#1A231E]">
              <div className="w-10 h-10 rounded-xl bg-[#FBEFE9] border border-[#F2D9CC] flex items-center justify-center">
                <Mail className="w-4 h-4 text-[#C86646]" />
              </div>
              <span className="font-medium">{content.contact_email}</span>
            </div>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          onSubmit={submit}
          className="zcard p-6 sm:p-8 space-y-4"
          data-testid="contact-form"
        >
          <div>
            <label className="block text-xs font-bold text-[#1A231E] mb-1.5 uppercase tracking-wide">Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={update("name")}
              placeholder="Your name"
              className="w-full px-4 py-3 rounded-xl border border-[#E4E1D9] focus:border-[#1E3F33] focus:ring-2 focus:ring-[#EAF1ED] outline-none transition bg-white"
              data-testid="contact-name-input"
              required
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-[#1A231E] mb-1.5 uppercase tracking-wide">Mobile *</label>
              <input
                type="tel"
                value={form.mobile}
                onChange={update("mobile")}
                placeholder="+91 98765 43210"
                className="w-full px-4 py-3 rounded-xl border border-[#E4E1D9] focus:border-[#1E3F33] focus:ring-2 focus:ring-[#EAF1ED] outline-none transition bg-white"
                data-testid="contact-mobile-input"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#1A231E] mb-1.5 uppercase tracking-wide">Email (optional)</label>
              <input
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="you@example.com"
                className="w-full px-4 py-3 rounded-xl border border-[#E4E1D9] focus:border-[#1E3F33] focus:ring-2 focus:ring-[#EAF1ED] outline-none transition bg-white"
                data-testid="contact-email-input"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-[#1A231E] mb-1.5 uppercase tracking-wide">Message *</label>
            <textarea
              value={form.message}
              onChange={update("message")}
              rows={4}
              placeholder="Tell us how we can help…"
              className="w-full px-4 py-3 rounded-xl border border-[#E4E1D9] focus:border-[#1E3F33] focus:ring-2 focus:ring-[#EAF1ED] outline-none transition resize-none bg-white"
              data-testid="contact-message-input"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full justify-center"
            data-testid="contact-submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {loading ? "Sending…" : "Submit Enquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
